import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { recordHyperDXException } from "./hyperdx";

type CaptureContext = Parameters<typeof Sentry.captureException>[1];

interface AppErrorContext extends CaptureContext {
  hyperdx?: {
    attributes?: Record<string, unknown>;
  };
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error("Unknown application error");
}

function buildHyperDXAttributes(context?: AppErrorContext) {
  if (!context) {
    return undefined;
  }

  const attributes: Record<string, unknown> = {};

  if (context.level) {
    attributes.level = context.level;
  }

  if (context.tags) {
    Object.assign(attributes, context.tags);
  }

  if (context.extra) {
    Object.assign(attributes, context.extra);
  }

  if (context.contexts) {
    Object.assign(attributes, { contexts: context.contexts });
  }

  if (context.hyperdx?.attributes) {
    Object.assign(attributes, context.hyperdx.attributes);
  }

  return Object.keys(attributes).length > 0 ? attributes : undefined;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn("Sentry DSN is missing");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  });
}

export function captureAppException(
  error: unknown,
  context?: AppErrorContext,
) {
  const normalizedError = normalizeError(error);
  const sentryContext = context ? { ...context } : undefined;
  const hyperDXAttributes = buildHyperDXAttributes(context);

  if (sentryContext && "hyperdx" in sentryContext) {
    delete sentryContext.hyperdx;
  }

  Sentry.captureException(normalizedError, sentryContext);
  recordHyperDXException(normalizedError, hyperDXAttributes);
}

export function createReactErrorHandler() {
  return (error: unknown, errorInfo: { componentStack?: string }) => {
    captureAppException(error, {
      tags: { feature: "react_error_boundary" },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  };
}
